import { computed, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
import { extraPopulationTokens } from "@/components/Survey/ResultsV2/cardOptionRules";
import type { DisplayedPopulation, SavedPopulation, ViewPopulation } from "@/types";

type CardOptions = { custom_populations?: string[] | null; population?: string | null } | null | undefined;

// The populations a card works on: "All" first, then the view's populations (quick
// filters included) and the saved ones its `custom_populations` adds; and the one its
// `population` option picks. Shared by question cards (useCardData) and the
// analysis cards that compute on a single population (useCardPopulation).
export function useViewPopulations(card: Ref<{ options?: CardOptions }>, localOptions: Ref<CardOptions> | null = null) {
  const { effectiveShowAllPopulation, savedPopulations } = storeToRefs(useResultsV2Store());
  const populationsStore = useResultsV2PopulationsStore();
  const { populations: viewPopulations } = storeToRefs(populationsStore);

  const options = computed<CardOptions>(() => localOptions?.value || card.value?.options);
  const customPopulations = computed(() => options.value?.custom_populations || null);
  const populationOption = computed(() => options.value?.population || null);

  const resolvedPopulations = computed<{ showAll: boolean; pops: DisplayedPopulation[] }>(() => {
    // Asterisk marks modified/unsaved populations in the legend; `id` resolves bar clicks.
    const pops: DisplayedPopulation[] = (viewPopulations.value as ViewPopulation[]).map((pop, index) => ({
      id: pop.id,
      token: pop.search_group?.token || "",
      key: `pop_sg_${pop.search_group?.token}`,
      label: `${pop.search_group?.name || `Population ${index + 1}`}${populationsStore.isNarrowed(pop) ? "*" : ""}`,
      color: pop.search_group?.color || POPULATION_AUTO_COLORS[(index + 1) % POPULATION_AUTO_COLORS.length],
      filters: pop.search_group?.filters || {},
    }));

    for (const token of extraPopulationTokens(customPopulations.value, pops.map((pop) => pop.token))) {
      if (pops.some((pop) => pop.token === token)) continue;
      const sp = (savedPopulations.value as SavedPopulation[]).find((s) => s.token === token);
      if (!sp) continue;
      pops.push({
        token: sp.token,
        key: `pop_sg_${sp.token}`,
        label: sp.name,
        color: sp.color || POPULATION_AUTO_COLORS[(pops.length + 1) % POPULATION_AUTO_COLORS.length],
        filters: sp.filters || {},
      });
    }

    return { showAll: effectiveShowAllPopulation.value, pops };
  });

  const displayedPopulations = computed<DisplayedPopulation[]>(() => {
    const { showAll, pops } = resolvedPopulations.value;
    const all: DisplayedPopulation = { id: "all", token: "all", key: null, label: "All", color: POPULATION_AUTO_COLORS[0], filters: {} };
    return showAll ? [all, ...pops] : pops;
  });

  // The picked population when it is displayed, else the first displayed one.
  const selectedPopulation = computed<DisplayedPopulation | null>(() => {
    const displayed = displayedPopulations.value;
    return displayed.find((pop) => pop.token === populationOption.value) || displayed[0] || null;
  });

  return { resolvedPopulations, displayedPopulations, selectedPopulation };
}
